import { useCallback, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import type { StoreFollowUp } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { useChatStoreActions } from '../chat-store/useChatStoreActions';

import { createSeededConversationFromAction } from './create-seeded-conversation-from-action';
import { getActionFollowUps } from './getActionFollowUps';

import { useActiveAction } from './index';

type ChatStoreActions = ReturnType<typeof useChatStoreActions>;
type SendMessage = ChatStoreActions['sendMessage'];
type SendMessageParams = Parameters<SendMessage>[0];

interface UseActiveActionSeededFollowUpInput {
	activeAction: ConversationAction | undefined;
	agent: SendMessageParams['agent'];
	storeUserMessages: SendMessageParams['storeUserMessages'];
	webSearchEnabled: SendMessageParams['webSearchEnabled'];
	addCurrentPageAsContextUserPreference: SendMessageParams['addCurrentPageAsContextUserPreference'];
	appFilters: SendMessageParams['appFilters'];
}

export const useActiveActionSeededFollowUp = ({
	activeAction,
	agent,
	storeUserMessages,
	webSearchEnabled,
	addCurrentPageAsContextUserPreference,
	appFilters,
}: UseActiveActionSeededFollowUpInput): {
	followUp: StoreFollowUp | undefined;
	onFollowUpClick: (prompt: string) => Promise<void>;
} => {
	di(useChatStoreActions, useActiveAction);

	const { createSeededConversation, sendMessage } = useChatStoreActions();
	const { setActiveAction } = useActiveAction();
	const canSeedConversation = activeAction?.customFollowUpContext?.available === true;

	const followUp = useMemo(() => {
		if (!canSeedConversation) {
			return undefined;
		}

		const followUps = getActionFollowUps(activeAction);
		if (followUps.length === 0) {
			return undefined;
		}
		return {
			type: 'FOLLOW_UP',
			content: {
				type: 'FOLLOW_UP_QUERIES',
				message: {
					follow_ups: followUps.map((text) => ({
						type: 'SYNTHETIC' as const,
						text,
					})),
				},
				millisOffset: 0,
			},
			id: -1,
		} satisfies StoreFollowUp;
	}, [activeAction, canSeedConversation]);

	const onFollowUpClick = useCallback(
		async (prompt: string) => {
			const customFollowUpContext = activeAction?.customFollowUpContext;
			if (!canSeedConversation || !customFollowUpContext?.available) {
				return;
			}

			const dialogues = await customFollowUpContext.fetch();
			if (!dialogues) {
				return;
			}

			const newConversationId = await createSeededConversationFromAction({
				action: activeAction,
				dialogues,
				createSeededConversation,
				fallbackName: activeAction.title?.trim() || undefined,
				defaultFollowUpSource: 'ai-smart-button-dynamic-surface',
				agentId: agent?.id,
			});

			if (!newConversationId) {
				return;
			}

			// Engage succeeds via `onCreated`; only remove the view, not dismiss state.
			setActiveAction(undefined);

			sendMessage({
				agent,
				prompt: encodeAdf(prompt),
				conversationId: newConversationId,
				storeUserMessages,
				messageSources: {
					interactionSource: 'followUps',
					followUpSource: 'ai-mate',
				},
				webSearchEnabled,
				addCurrentPageAsContextUserPreference,
				appFilters,
			});
		},
		[
			activeAction,
			agent,
			appFilters,
			addCurrentPageAsContextUserPreference,
			canSeedConversation,
			createSeededConversation,
			sendMessage,
			setActiveAction,
			storeUserMessages,
			webSearchEnabled,
		],
	);

	return { followUp, onFollowUpClick };
};
